<template>
  <div>
    <CreateAnnouncementDialog
      v-model="showAddDialog"
      @created="handleAnnouncementCreated"
    />
    <EditAnnouncementDialog
      v-model="showEditDialog"
      :announcement="selectedAnnouncement"
      @updated="handleAnnouncementUpdated"
    />
    <DeleteAnnouncementDialog
      v-model="showDeleteDialog"
      :announcement="selectedAnnouncementToDelete"
      @deleted="handleAnnouncementDeleted"
    />
    <UnpublishAnnouncementDialog
      v-model="showUnpublishDialog"
      :announcement="selectedAnnouncementToUnpublish"
      @unpublished="handleAnnouncementUnpublished"
    />
    <AnnouncementViewsDialog
      v-model="showViewsDialog"
      :announcement="selectedAnnouncementForViews"
    />

    <div class="table-container">
      <v-card flat>
        <v-data-table
          :headers="headers"
          :items="announcements"
          :items-per-page="50"
          class="elevation-0 custom-data-table"
          :no-data-text="noDataText"
          :height="tableHeight"
          fixed-header
          :loading="tableLoading"
          density="compact"
          @click:row="handleRowClick"
        >
          <template v-slot:top>
            <div class="d-flex justify-space-between align-center pa-4">
              <span class="text-h5">Announcements</span>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="openAddDialog"
                >Add Announcement</v-btn
              >
            </div>
          </template>
          <template v-slot:[`item.announcement_message`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  v-bind="props"
                  class="announcement-message-preview"
                  v-html="truncateMessage(item.announcement_message)"
                ></div>
              </template>
              <span
                style="
                  max-width: 400px;
                  display: inline-block;
                  word-wrap: break-word;
                "
                >{{ getFullMessageText(item.announcement_message) }}</span
              >
            </v-tooltip>
          </template>
          <template v-slot:[`item.created_on`]="{ item }">
            {{ formatTimestamp(item.created_on) }}
          </template>
          <template v-slot:[`item.views`]="{ item }">
            <div
              class="d-flex align-center"
              style="cursor: pointer;"
              @click.stop="openViewsDialog(item)"
            >
              <v-icon size="small" color="primary" v-if="item.views > 0">mdi-eye</v-icon>
              <span class="ml-1">{{ item.views || 0 }}</span>
            </div>
          </template>
<template v-slot:[`item.visible_for_name`]="{ item }">
  {{ item.visible_for_name?.length > 0 ? item.visible_for_name.join(", ") : "-" }}
</template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-menu location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  v-bind="props"
                  @click.stop
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item title="Edit" @click.stop="openEditDialog(item)">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="small">mdi-pencil</v-icon>
                  </template>
                </v-list-item>
                <v-list-item
                  v-if="item.announcement_status === 'PUBLISH'"
                  title="Unpublish"
                  @click.stop="openUnpublishDialog(item)"
                >
                  <template v-slot:prepend>
                    <v-icon color="warning" size="small">mdi-eye-off</v-icon>
                  </template>
                </v-list-item>
                <v-list-item
                  v-if="item.announcement_status === 'UNPUBLISH'"
                  title="Publish"
                  @click.stop="openPublishDialog(item)"
                >
                  <template v-slot:prepend>
                    <v-icon color="success" size="small">mdi-eye</v-icon>
                  </template>
                </v-list-item>
                <v-list-item
                  v-if="item.announcement_status === 'UNPUBLISH'"
                  title="Delete"
                  @click.stop="openDeleteDialog(item)"
                >
                  <template v-slot:prepend>
                    <v-icon color="error" size="small">mdi-delete</v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div> <!-- End of table container -->
</template>

<script>
import GetAnnouncements from "@/mixins/Announcement/GetAnnouncements";
import CreateAnnouncementDialog from "@/components/Announcement/Dailogs/CreateAnnouncementDialog.vue";
import EditAnnouncementDialog from "@/components/Announcement/Dailogs/EditAnnouncementDialog.vue";
import DeleteAnnouncementDialog from "@/components/Announcement/Dailogs/DeleteAnnouncementDialog.vue";
import UnpublishAnnouncementDialog from "@/components/Announcement/Dailogs/UnpublishAnnouncementDialog.vue";
import AnnouncementViewsDialog from "@/components/Announcement/Dailogs/AnnouncementViewsDialog.vue";

export default {
  name: "AnnouncementPage",
  mixins: [GetAnnouncements],
  components: {
    CreateAnnouncementDialog,
    EditAnnouncementDialog,
    DeleteAnnouncementDialog,
    UnpublishAnnouncementDialog,
    AnnouncementViewsDialog,
  },
  data() {
    return {
      tableLoading: false,
      windowHeight: window.innerHeight,
      currentPage: 1,
      headers: [
        { title: "Message", key: "announcement_message", sortable: true },

        { title: "Status", key: "announcement_status", sortable: true },
        { title: "Created Date", key: "created_on", sortable: true },
        { title: "Organization", key: "visible_for_name", sortable: true },
        { title: "Views", key: "views", sortable: true },
        { title: "Actions", key: "actions", sortable: false },
      ],  
      showAddDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      showUnpublishDialog: false,
      showViewsDialog: false,

      selectedAnnouncement: null,
      selectedAnnouncementToDelete: null,
      selectedAnnouncementToUnpublish: null,
      selectedAnnouncementForViews: null,
    };
  },
  computed: {
    announcements() {
      return this.announcementsList || [];
    },
    noDataText() {
      return "No announcements found. Click 'Add Announcement' to add one.";
    },
    tableHeight() {
      return this.windowHeight - 170;
    },
  },
  async mounted() {
    this.updateWindowHeight();
    window.addEventListener("resize", this.updateWindowHeight);
    this.tableLoading = true;
    await this.fetchAnnouncements();
    this.tableLoading = false;
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowHeight);
  },
  methods: {
    truncateMessage(message) {
      if (!message) return "";
      // Strip HTML tags for preview
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = message;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";
      // Return first line (approximately 80 characters) with ellipsis
      if (textContent.length > 50) {
        return textContent.substring(0, 50) + "...";
      }
      return textContent;
    },
    getFullMessageText(message) {
      if (!message) return "";
      // Strip HTML tags to get plain text for tooltip
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = message;
      return tempDiv.textContent || tempDiv.innerText || "";
    },
    formatTimestamp(timestamp) {
      const numeric = Number(timestamp);
      if (isNaN(numeric)) return "";
      const ms = numeric < 1e12 ? numeric * 1000 : numeric;
      const date = new Date(ms);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        // hour: "2-digit",
        // minute: "2-digit",
        // hour12: true,
      });
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    openAddDialog() {
      this.showAddDialog = true;
    },
    async handleAnnouncementCreated() {
      await this.fetchAnnouncements();
    },
    async handleAnnouncementUpdated() {
      await this.fetchAnnouncements();
    },
    async handleAnnouncementDeleted() {
      await this.fetchAnnouncements();
    },
    async handleAnnouncementUnpublished() {
      await this.fetchAnnouncements();
    },
    handleRowClick(event, { item }) {
      this.openEditDialog(item);
    },
    openEditDialog(item) {
      this.selectedAnnouncement = item;
      this.showEditDialog = true;
    },
    openDeleteDialog(item) {
      this.selectedAnnouncementToDelete = item;
      this.showDeleteDialog = true;
    },
    openUnpublishDialog(item) {
      this.selectedAnnouncementToUnpublish = item;
      this.showUnpublishDialog = true;
    },
    openViewsDialog(item) {
      this.selectedAnnouncementForViews = item;
      this.showViewsDialog = true;
    },

    async openPublishDialog(item) {
      // Directly publish without dialog confirmation
      try {
        const { generateClient } = await import("aws-amplify/api");
        const client = generateClient();
        const { edit_announcement } = await import("@/graphql/mutations");

        const input = {
          announcement_id: item.announcement_id,
          announcement_status: "PUBLISH",
          announcement_source: "BMS",
        };

        const response = await client.graphql({
          query: edit_announcement,
          variables: {
            input: input,
          },
        });

        const responseData = JSON.parse(response.data.edit_announcement);

        if (responseData.status === "SUCCESS") {
          await this.fetchAnnouncements();
        }
      } catch (error) {
        console.error("Error publishing announcement:", error);
      }
    },
  },
};
</script>

<style scoped>
</style>
